/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useCallback, useContext, useMemo, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { ButtonItem, MenuGroup } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { Divider, MenuSection, MenuSectionHeading } from '@atlassian/navigation-system';

import { MAX_ALLOWED_SECTIONS } from '../../constants';
import { useUniversalCreateApi, withIntlProvider } from '../../services';
import type { MenuItem, NamedItems } from '../../types';
import { filterCrossAppItems, MenuControlContext } from '../../utils';

import MenuContentItem from './menu-content-item';

const styles = cssMap({
	menuWrap: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},

	itemWrap: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},

	textStyle: {
		fontWeight: token('font.weight.medium'),
		fontFamily: token('font.family.body'),
		color: token('color.text.subtle'),
	},
});

const MAX_ITEMS = 10;
const MIN_ITEMS = 4;

const showMoreButtonWrap = css({
	paddingLeft: token('space.100', '8px'),
	paddingRight: token('space.100', '8px'),

	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	'.custom-item-wrapper-style': {
		borderRadius: '4px',
		paddingLeft: token('space.100', '8px'),
		paddingRight: token('space.100', '8px'),
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		svg: {
			color: token('color.text.subtle'),
		},
	},
});

const messages = defineMessages({
	showMore: {
		id: 'platform.navigation-experiences.universal-create.show-more',
		defaultMessage: 'More',
	},
	showLess: {
		id: 'platform.navigation-experiences.universal-create.show-less',
		defaultMessage: 'Less',
	},
	remoteSectionHeading: {
		id: 'platform.navigation-experiences.universal-create.remote-section-heading',
		defaultMessage: 'Suggestions',
	},
});

function MenuContent() {
	const { formatMessage } = useIntl();

	const { mixedItems } = useContext(MenuControlContext);
	const crossAppCreationEnabled = fg('navx_universal_create_cross_app_creation_enabled');
	const { items: crossAppItems } = useUniversalCreateApi();

	const allowedItems = useMemo(() => {
		const items = mixedItems.slice(0, MAX_ALLOWED_SECTIONS);
		const filteredCrossAppItems = filterCrossAppItems(mixedItems, crossAppItems);
		if (crossAppCreationEnabled && filteredCrossAppItems.length > 0) {
			items.push({
				heading: formatMessage(messages.remoteSectionHeading),
				items: filteredCrossAppItems,
				foldable: true,
			});
		}
		return items;
	}, [formatMessage, crossAppCreationEnabled, crossAppItems, mixedItems]);

	const [showAll, setShowAll] = useState(false);

	const text = showAll ? formatMessage(messages.showLess) : formatMessage(messages.showMore);

	const handleShowMore = useCallback(() => {
		setShowAll((prev) => !prev);
	}, [setShowAll]);

	const renderItems = useCallback(
		(items: MenuItem[], foldable?: boolean) => {
			if (!foldable || items.length <= MIN_ITEMS) {
				return items;
			}

			return items.slice(0, showAll ? MAX_ITEMS : MIN_ITEMS);
		},
		[showAll],
	);

	const restProps = {
		className: 'custom-item-wrapper-style',
	};

	if (!allowedItems?.length) {
		return null;
	}

	return (
		<MenuGroup spacing="compact" testId="global-create-content-popover-menu">
			<Box xcss={cx(styles.menuWrap)}>
				{allowedItems.map((namedItem: NamedItems, sectionIndex: number) => (
					<Fragment key={`menu-section-${sectionIndex}`}>
						<MenuSection>
							{namedItem.heading && (
								<Box xcss={cx(styles.itemWrap)}>
									<MenuSectionHeading>{namedItem.heading}</MenuSectionHeading>
								</Box>
							)}
							{renderItems(namedItem.items, namedItem.foldable).map(
								(menuItem: MenuItem, index: number) => (
									<MenuContentItem
										item={menuItem}
										key={`menu-section-${sectionIndex}-item-${index}`}
									/>
								),
							)}
							{namedItem.foldable && namedItem.items.length > MIN_ITEMS && (
								<div css={showMoreButtonWrap}>
									<ButtonItem
										{...restProps}
										iconBefore={<ShowMoreHorizontalIcon label="" color="var(--ds-text-subtle)" />}
										onClick={handleShowMore}
										testId="toggle-more-less-button"
									>
										<Box as="span" xcss={styles.textStyle}>
											{text}
										</Box>
									</ButtonItem>
								</div>
							)}
						</MenuSection>
						{sectionIndex !== allowedItems.length - 1 ? <Divider /> : null}
					</Fragment>
				))}
			</Box>
		</MenuGroup>
	);
}

export default withIntlProvider(MenuContent);
